import * as ivs from 'amazon-ivs-player';
import { isObject } from 'underscore';

import { ResourcePool } from './use-resource-pool';
import multiplierFibonacci from '../multiplier-fibonacci';

export const getVideoDuration = async (
  mediaUrl: string,
  mediaType: string,
  paramVideo: HTMLVideoElement,
  testPlayer?: ResourcePool
) => {
  const video = paramVideo;

  const returnedDuration: number = await new Promise<number>((resolve, reject) => {
    try {
      if ((mediaType === 'hls' || mediaUrl.endsWith('.m3u8')) && testPlayer) {
        const playerContainer = testPlayer?.getElement();

        if (!playerContainer || !isObject(playerContainer) || !playerContainer.data) {
          reject(new Error('Player not found'));
          return;
        }

        const player = playerContainer.data;

        player.attachHTMLVideoElement(video);

        let duration = 0;
        let bufferAssumption = 1; // This is in seconds
        let playInterval: NodeJS.Timeout;
        let retryInterval = 100;

        const playAfterSeek = () => {
          // after we seek we want to play
          // in order for buffering to start
          player.addEventListener(ivs.PlayerEventType.BUFFER_UPDATE, bufferUpdateResolve);

          if (playInterval) {
            clearInterval(playInterval);
          }

          // sometimes play does not trigger buffer so we retry
          playInterval = setInterval(() => {
            retryInterval = multiplierFibonacci(retryInterval, 100);

            if (player.isPaused()) {
              player.play();
            } else {
              player.pause();
              bufferAssumption += 1;
              player.seekTo(duration - bufferAssumption);
            }
          }, retryInterval);
        };

        const durationChanged = () => {
          duration = player.getDuration() || 0;
          player.addEventListener(ivs.PlayerEventType.SEEK_COMPLETED, playAfterSeek);
          player.removeEventListener(ivs.PlayerEventType.DURATION_CHANGED, durationChanged);
          // We seek to near the end to prepare to buffer
          // since buffering will give us the true value of the duration.
          player.seekTo(duration - bufferAssumption);
        };

        const bufferUpdateResolve = () => {
          if (playInterval) {
            clearInterval(playInterval);
          }

          player.pause();

          // We have the buffer duration to compare against
          player.removeEventListener(ivs.PlayerEventType.SEEK_COMPLETED, playAfterSeek);
          player.removeEventListener(ivs.PlayerEventType.DURATION_CHANGED, durationChanged);
          player.removeEventListener(ivs.PlayerEventType.BUFFER_UPDATE, bufferUpdateResolve);

          const bufferDuration = player.getBufferDuration();

          /*
						Checking for undefined this way for clarity and since we
						don't want it to fail for the falsy value
						`bufferDuration === 0` - but we also don't want to let
						negative numbers through.
					*/
          if (bufferDuration !== undefined && bufferDuration >= 0) {
            if (playerContainer?.data?.core?.id) {
              testPlayer.releaseElement(playerContainer.data.core.id);
            }
            resolve(duration - (bufferAssumption - bufferDuration));
          } else {
            // player.getBufferDuration() gave undefined so we need to seek backward more
            bufferAssumption += 1;
            player.addEventListener(ivs.PlayerEventType.SEEK_COMPLETED, playAfterSeek);

            player.seekTo(duration - bufferAssumption);
          }
        };

        player.addEventListener(ivs.PlayerEventType.DURATION_CHANGED, durationChanged);

        // don't want to accidentally play audio
        player.setVolume(0);
        player.load(mediaUrl);
      } else {
        video.onloadedmetadata = () => {
          const duration = video.duration || 0;
          window.URL.revokeObjectURL(video.src);
          resolve(duration);
        };

        video.src = mediaUrl;
      }
    } catch (e) {
      // weird interaction where e can't be Error type
      console.error('getVideoDuration failed', { error: e });
      reject(e);
    }
  });

  return returnedDuration;
};

export default getVideoDuration;
